import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon, UserGroupIcon, UsersIcon, AcademicCapIcon,
  CalendarDaysIcon, ChartBarIcon, MegaphoneIcon,
  UserCircleIcon, XMarkIcon, ClipboardDocumentListIcon, ShieldCheckIcon,
  TableCellsIcon, CalendarIcon, ArrowRightOnRectangleIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';
import { useSchoolModules } from '../../hooks/useSchoolModules';

const buildNAV = (obeEnabled) => [
  { section:'Main',       name:'Dashboard',           href:'/coordinator/dashboard',           icon:HomeIcon },
  { section:'Management', name:'Students',             href:'/coordinator/students',            icon:UserGroupIcon },
  { section:'Management', name:'Teachers',             href:'/coordinator/teachers',            icon:UsersIcon },
  { section:'Management', name:'Parent Accounts',      href:'/coordinator/parents',             icon:UserGroupIcon },
  { section:'Management', name:'Classes',              href:'/coordinator/classes',             icon:AcademicCapIcon },
  { section:'Attendance', name:'Student Attendance',   href:'/coordinator/student-attendance',  icon:CalendarDaysIcon },
  { section:'Attendance', name:'My Attendance',        href:'/coordinator/own-attendance',      icon:CalendarIcon },
  { section:'Academic',   name:'Student Left',         href:'/coordinator/student-left',        icon:ArrowRightOnRectangleIcon },
  { section:'Academic',   name:'Staff Exits',          href:'/coordinator/staff-exits',         icon:ArrowRightOnRectangleIcon },
  { section:'Academic',   name:'Class Promotion',      href:'/coordinator/class-promotion',     icon:AcademicCapIcon },
  { section:'Academic',   name:'Year-End Cleanup',     href:'/coordinator/year-end-cleanup',    icon:TrashIcon },
  { section:'Academic',   name:'Salary',               href:'/coordinator/salary',              icon:ChartBarIcon },
  { section:'Academic',   name:'My Leaves',            href:'/coordinator/own-leaves',          icon:ClipboardDocumentListIcon },
  { section:'Academic',   name:'Holidays',             href:'/coordinator/holidays',            icon:CalendarDaysIcon },
  { section:'Content',    name:'Materials',            href:'/coordinator/materials',           icon:ClipboardDocumentListIcon },
  { section:'Content',    name:'Assessment Types',     href:'/coordinator/assessment-types',    icon:ClipboardDocumentListIcon },
  { section:'Content',    name:'Terms',                href:'/coordinator/term-periods',        icon:CalendarDaysIcon },
  { section:'Content',    name:'Subject Split',        href:'/coordinator/term-subject-split',  icon:CalendarDaysIcon },
  { section:'Content',    name:'Assessments',          href:'/coordinator/assessments',         icon:ClipboardDocumentListIcon },
  { section:'Content',    name:'Grade Reports',        href:'/coordinator/grade-report',        icon:ChartBarIcon },
  { section:'Content',    name:'Timetable',            href:'/coordinator/timetable',           icon:TableCellsIcon },
  { section:'Content',    name:'Notices',              href:'/coordinator/notices',             icon:MegaphoneIcon, badge:true },
  { section:'Content',    name:'Feedback',             href:'/coordinator/feedback',            icon:ChartBarIcon },
  // OBE — only when enabled
  ...(obeEnabled ? [
    { section:'OBE',      name:'CLO Management',       href:'/coordinator/clo-management',      icon:ChartBarIcon },
    { section:'OBE',      name:'CLO Attainment',       href:'/coordinator/attainment',          icon:ChartBarIcon },
    { section:'OBE',      name:'PLO Management',       href:'/coordinator/plo-management',      icon:ChartBarIcon },
    { section:'OBE',      name:'PLO Attainment',       href:'/coordinator/plo-attainment',      icon:ChartBarIcon },
  ] : []),
  { section:'Account',    name:'My Requests',          href:'/coordinator/requests',            icon:ClipboardDocumentListIcon },
  { section:'Account',    name:'Access Panel',         href:'/coordinator/access',              icon:ShieldCheckIcon },
  { section:'Account',    name:'Profile',              href:'/coordinator/profile',             icon:UserCircleIcon },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .csb { width:240px; min-width:240px; min-height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .csb-close { display:none; }
  @media(max-width:768px){
    .csb-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; }
    .csb-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
    .csb-brand { position:relative; }
  }
  .csb-brand { padding:24px 20px 20px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; }
  .csb-brand-icon { width:34px; height:34px; background:#f97316; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:10px; }
  .csb-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .csb-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .csb-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .csb-nav { flex:1; padding:12px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .csb-nav::-webkit-scrollbar { display:none; }
  .csb-section-label { font-size:.62rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:16px 8px 6px; }
  .csb-link { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.82rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:2px; position:relative; }
  .csb-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .csb-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .csb-link.active { background:rgba(249,115,22,.12); color:#f97316; }
  .csb-link.active::before { content:''; position:absolute; left:-12px; top:25%; bottom:25%; width:3px; background:#f97316; border-radius:0 3px 3px 0; }
  .csb-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .csb-footer { padding:16px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .csb-footer-text { font-size:.68rem; color:#353b5c; text-align:center; }
  .notice-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; }
`;

const CoordinatorSidebar = ({ onClose }) => {
  const location      = useLocation();
  const { brand }     = useSchoolBrand();
  const { cloPloEnabled } = useSchoolModules();
  const [noticeCount, setNoticeCount] = useState(0);
  const [obeEnabled,  setObeEnabled]  = useState(false);

  useEffect(() => {
    api.get('/coordinator/notices/unread-count')
      .then(r => setNoticeCount(r.data.count || 0))
      .catch(() => {});
    api.get('/coordinator/obe-settings')
      .then(r => setObeEnabled(!!r.data?.obeEnabled && !!r.data?.obeCoordEnabled))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/coordinator/notices')) {
      api.post('/coordinator/notices/mark-read').catch(() => {});
      setNoticeCount(0);
    }
  }, [location.pathname]);

  // OBE nav requires both the Admin-level obe_enabled toggle AND the
  // Super-Admin cloPloEnabled module switch.
  const NAV = buildNAV(obeEnabled && cloPloEnabled);

  const sectionOrder = ['Main','Management','Attendance','Academic','Content','OBE','Account'];
  const sections = sectionOrder
    .map(sec => ({ sec, items: NAV.filter(n => n.section === sec) }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      <style>{style}</style>
      <div className="csb">
        <div className="csb-brand">
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="csb-brand-icon">
                  <svg viewBox="0 0 24 24"><path d="M12 3L2 9l10 6 10-6-10-6zM2 15l10 6 10-6M2 12l10 6 10-6" /></svg>
                </div>
            }
            <div>
              <div className="csb-brand-name">{brand.name || 'School Portal'}</div>
              <div className="csb-brand-sub">Coordinator Panel</div>
            </div>
          </div>
          {onClose && <button className="csb-close" onClick={onClose}><XMarkIcon /></button>}
        </div>

        <nav className="csb-nav">
          {sections.map(({ sec, items }) => (
            <div key={sec}>
              <div className="csb-section-label">{sec}</div>
              {items.map(item => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link key={item.name} to={item.href}
                    className={`csb-link${isActive ? ' active' : ''}`}
                    onClick={onClose}>
                    <item.icon />
                    <span className="csb-link-text">{item.name}</span>
                    {item.badge && noticeCount > 0 && (
                      <span className="notice-badge">{noticeCount > 99 ? '99+' : noticeCount}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="csb-footer">
          <div className="csb-footer-text">v1.0.0 · Coordinator Portal</div>
        </div>
      </div>
    </>
  );
};

export default CoordinatorSidebar;